import * as React from 'react';

type BeforeInstallPromptEvent = Event & {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed'; platform: string }>;
};

function isStandalone() {
  return window.matchMedia?.('(display-mode: standalone)').matches || (navigator as any).standalone;
}

function isIOS() {
  const ua = window.navigator.userAgent.toLowerCase();
  const isApple = /iphone|ipad|ipod/.test(ua);
  const isSafari = /safari/.test(ua) && !/crios|fxios|edgios|chrome/.test(ua);
  return isApple && isSafari;
}

export function usePwaInstall() {
  const [deferredPrompt, setDeferredPrompt] = React.useState<BeforeInstallPromptEvent | null>(null);

  const [installed, setInstalled] = React.useState<boolean>(isStandalone());
  const [ios, setIos] = React.useState<boolean>(false);

  React.useEffect(() => {
    setIos(isIOS());

    const onBeforeInstallPrompt = (e: Event) => {
      e.preventDefault();
      setDeferredPrompt(e as BeforeInstallPromptEvent);
    };

    const onAppInstalled = () => {
      setInstalled(true);
      setDeferredPrompt(null);
    };

    window.addEventListener('beforeinstallprompt', onBeforeInstallPrompt);
    window.addEventListener('appinstalled', onAppInstalled);

    return () => {
      window.removeEventListener('beforeinstallprompt', onBeforeInstallPrompt);
      window.removeEventListener('appinstalled', onAppInstalled);
    };
  }, []);

  const canPrompt = !!deferredPrompt && !installed;

  const promptInstall = React.useCallback(async () => {
    if (!deferredPrompt) return { ok: false as const, reason: 'no_prompt' as const };

    await deferredPrompt.prompt();
    const choice = await deferredPrompt.userChoice;

    if (choice.outcome === 'accepted') {
      setDeferredPrompt(null);
      return { ok: true as const };
    }
    return { ok: false as const, reason: 'dismissed' as const };
  }, [deferredPrompt]);

  return {
    installed,
    ios,
    canPrompt,
    promptInstall,
  };
}
